import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import downloadIcon from "@/assets/icons/downloaIcon.svg";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { GtmVideoList } from "./GtmPopUp";
import moment from "moment";
import { useRouter } from "next/router";
import Actions from "@/elements/Actions";
import { MdDisplaySettings, MdInsertLink } from "react-icons/md";
import { TradeCardBuyBtn, TradeCardExitBtn } from "@/components/PlaceOrder/BuySellBtn";
import { isFutures, isFuturesOrOptions, isMcxFuturesOrOptions, isOptions } from "@/lib/tradeCards";
import { cn, putComma } from "@/lib/utils";
import { useDispatch } from "react-redux";
import { updateExchange } from "@/store/features/stocksDetailsSlice";

const TradeHistoryPopUp = ({ ButtonCom = null, ele, children, openAboutTrade = false, tradeId = "" }) => {
    const dispatch = useDispatch();
    const router = useRouter();
    const [isOpen, setOpen] = useState(false);
    const isDisabled = ["MCX-FUT", "MCX-OPT", "COMMODITY"].includes(ele?.term);

    useEffect(() => {
        if (openAboutTrade && ele?.streamMessageId === tradeId) {
            setOpen(true);
        }
    }, [openAboutTrade, ele?.streamMessageId, tradeId]);

    const clickHandle = (lastparams) => {
        router.push(
            ele?.term === "SHORT"
                ? Actions.toStockDetail(
                      ele?.stockName ?? ele?.nseSymbol ?? ele?.bseSymbol,
                      ele?.compName ?? ele?.subtitle,
                      lastparams
                  )
                : ele?.segment === "I"
                ? Actions.toIndicesDetail(ele?.stockName ?? ele?.nseSymbol ?? ele?.bseSymbol, ele?.compName, lastparams)
                : Actions.toFNODetail(`${ele?.parentFinCode}-${ele.finCode}`, lastparams)
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={setOpen}>
            <DialogTrigger
                className="text-base font-bold cursor-pointer border border-[#9D9D9D] w-full py-1.5 rounded-lg"
                asChild={!!ButtonCom}
            >
                {ButtonCom ? ButtonCom : "About trade"}
            </DialogTrigger>
            <DialogContent
                className="w-[600px] gap-4 flex flex-col font-Inter overflow-hidden"
                iconclassname="absolute "
            >
                <DialogHeader>
                    <DialogTitle className="text-center">About trade</DialogTitle>
                </DialogHeader>
                <div className="gap-4 flex flex-col max-h-[80vh] custom-scrollbar overflow-y-auto px-4">
                    {children}

                    {!ele.open && (
                        <>
                            <div className="whitespace-nowrap flex items-center justify-between self-stretch ">
                                <div className="flex flex-col justify-center items-start gap-1">
                                    <p className="text-[#747474] text-xs font-medium leading-5 flex flex-row gap-2 justify-center items-center">
                                        {ele?.modifiedStopLoss ? "Modified stoploss" : "Stoploss"}{" "}
                                    </p>
                                    <p className="text-black text-sm font-bold leading-6">
                                        {ele.stopLoss ? `₹${putComma(ele?.stopLoss)}` : "--"}
                                    </p>
                                </div>
                                {!isFuturesOrOptions(ele?.term) && ele.netAverageBuy && (
                                    <div className="flex flex-col justify-center items-start gap-1">
                                        <p className="text-[#747474] text-xs font-medium leading-5">Net average</p>
                                        <p className="text-black text-sm font-bold leading-6">
                                            {ele.netAverageBuy ? "₹" : "--"}
                                            {putComma(ele?.netAverageBuy)}
                                        </p>
                                    </div>
                                )}
                                <div className="flex flex-col justify-center items-start gap-1">
                                    <p className="text-[#747474] text-xs font-medium leading-5">
                                        {ele?.modifiedTarget ? "Modified target" : "Target"}
                                    </p>
                                    <p className="text-black text-sm font-bold leading-6">
                                        {ele.targetPrice ? "₹" : "--"}
                                        {putComma(ele?.targetPrice)}
                                    </p>
                                </div>
                                {isFuturesOrOptions(ele?.term) && ele?.lotSize && (
                                    <div className="flex flex-col justify-center items-center gap-1">
                                        <p className="text-[#747474] text-xs font-medium leading-5">
                                            {ele?.term?.includes("MCX-") ? "Multiplier" : "Lot size"}
                                        </p>
                                        <p className="text-black text-sm font-bold leading-6">
                                            {putComma(ele?.lotSize, 0)}
                                        </p>
                                    </div>
                                )}
                                {!isMcxFuturesOrOptions(ele?.term) && (
                                    <div className="flex flex-col justify-center items-end gap-1">
                                        <p className="text-[#747474] text-xs font-medium leading-5">
                                            {ele.term != "FUTURES" && ele.term != "OPTIONS" ? "Duration" : "Expiry"}
                                        </p>
                                        <p className="text-black text-sm font-bold leading-6">
                                            {ele?.expectedDurationEnum || "--"}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {isFuturesOrOptions(ele?.term) && (
                                <div
                                    className={cn(
                                        "-mt-4 py-1.5 px-3 bg-[#F5F5F5] border-t-[0.5px] border-[#747474] w-full flex justify-center items-center self-stretch rounded-b-lg",
                                        {
                                            "justify-between": isOptions(ele?.term),
                                            "rounded-b-none my-2 border-y-[0.5px] py-1": true
                                        }
                                    )}
                                >
                                    <div className="flex justify-center items-center gap-1.5 self-stretch px-0 py-1">
                                        <p className="text-[#414141] text-justify text-xs font-medium">Margin req. :</p>
                                        <p className="text-black text-justify text-xs font-bold">
                                            ₹{putComma(ele?.actualMarginRequired)}
                                        </p>
                                    </div>
                                    {isOptions(ele?.term) && (
                                        <div className="flex justify-center items-center gap-1.5 self-stretch px-0 py-1">
                                            <p className="text-[#414141] text-justify text-xs font-medium">
                                                Final margin :
                                            </p>
                                            <p className="text-black text-justify text-xs font-bold">
                                                ₹{putComma(ele?.liveMargin)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <div className="font-Inter flex justify-between items-center">
                        <div
                            className={`flex items-center gap-1 cursor-pointer ${
                                isDisabled ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            onClick={() => {
                                if (!isDisabled) {
                                    dispatch(updateExchange(ele?.exchange));
                                    clickHandle(``);
                                }
                            }}
                        >
                            <MdDisplaySettings className="w-4 h-4" color="#0862BC" />
                            <div className="font-semibold text-sm text-primary">Stock detail</div>
                        </div>
                        <div
                            className={`flex items-center gap-1 cursor-pointer ${
                                isDisabled ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            onClick={() => {
                                if (!isDisabled) {
                                    clickHandle("/charts");
                                }
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                            >
                                <path d="M2.25 7.72644V11.6364H5.9325V17.2734H9.8425V7.72644H2.25Z" fill="#0862BC" />
                                <path
                                    d="M13.1016 17.2734L17.1636 7.72644H21.7506L17.6886 17.2734H13.1016Z"
                                    fill="#0862BC"
                                />
                                <path
                                    d="M12.9199 11.6364C13.9996 11.6364 14.8749 10.7611 14.8749 9.68137C14.8749 8.60162 13.9996 7.72632 12.9199 7.72632C11.8401 7.72632 10.9648 8.60162 10.9648 9.68137C10.9648 10.7611 11.8401 11.6364 12.9199 11.6364Z"
                                    fill="#0862BC"
                                />
                            </svg>
                            <div className="font-semibold text-sm text-primary">Chart</div>
                        </div>
                        {ele?.optionEnabled && (
                            <div
                                className={`flex items-center gap-1 cursor-pointer ${
                                    isDisabled ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                onClick={() => {
                                    if (!isDisabled) {
                                        clickHandle("/option-chain");
                                    }
                                }}
                            >
                                <MdInsertLink className="w-5 h-5" color="#0862BC" />
                                <div className="font-semibold text-sm text-primary">Option chain</div>
                            </div>
                        )}
                    </div>
                    <hr />
                    {ele?.note && (
                        <>
                            <div className={cn("w-full bg-white rounded text-[#414141] text-xs font-semibold")}>
                                {ele?.note}
                            </div>
                            <hr />
                        </>
                    )}
                    {ele?.history && (
                        <>
                            <p className="text-black text-sm font-bold">Trade modification history</p>
                            {ele?.history.map((e, i) => (
                                <div
                                    key={i}
                                    className="flex flex-col items-start gap-2 self-stretch border bg-[#FFF] pb-3 rounded-xl border-solid border-[#CEE9FF]"
                                >
                                    <div className="flex items-start gap-2.5 self-stretch bg-[#DBEFFF] px-3 py-1 rounded-[12px_12px_0px_0px] flex-[1_0_0] text-[#606060] text-xs font-medium leading-5">
                                        {moment(e?.createdAt).format("DD MMM YY | hh:mm A")}
                                    </div>
                                    <p className="flex items-start gap-12 self-stretch px-3 py-0 text-black text-sm font-bold leading-6">
                                        {e?.title}
                                    </p>
                                    <p
                                        className="px-3 py-0 text-[#414141] text-xs font-normal leading-5"
                                        dangerouslySetInnerHTML={{ __html: e?.description }}
                                    />
                                    {e?.note && (
                                        <p
                                            className="px-3 py-0 text-[#414141] text-xs font-normal leading-5 border-t border-[#DBEFFF] pt-3"
                                            dangerouslySetInnerHTML={{ __html: `<b>Note:</b> ${e?.note}` }}
                                        />
                                    )}
                                </div>
                            ))}
                            <div className="flex flex-col items-start gap-2 self-stretch border bg-[#FFF] pb-3 rounded-xl border-solid border-[#CEE9FF]">
                                <div className="flex items-start gap-2.5 self-stretch bg-[#DBEFFF] px-3 py-1 rounded-[12px_12px_0px_0px] flex-[1_0_0] text-[#606060] text-xs font-medium leading-5">
                                    {moment(ele?.entryDateInLong ?? ele?.createdAt).format("DD MMM YY | hh:mm A")}
                                </div>
                                <p className="flex items-start gap-12 self-stretch px-3 py-0 text-black text-sm font-bold leading-6">
                                    Trade idea posted
                                </p>
                            </div>
                        </>
                    )}
                    {/* {(ele?.attachments || ele?.history) && !ele?.locked && (
                    <div className=" z-0 mt-[-60px] flex justify-between items-center self-stretch bg-[#C4E1F4] pt-[34px] pb-4 px-6 rounded-b-2xl">
                        {ele?.attachments ? (
                            <div onClick={handleDownload} className=" cursor-pointer flex items-center gap-2">
                                <Image src={downloadIcon} alt="" />
                                <p className="flex-[1_0_0] text-[#0862BC] text-xs font-semibold leading-5">
                                    {JSON.parse(ele?.attachments)?.attachments[0]?.key?.split("/").pop()}
                                </p>
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </div>
                )} */}
                    <GtmVideoList open={true} isTradeHistoryPopUp={true} />
                </div>
                <div className="flex items-start gap-4 self-stretch px-4">
                    <TradeCardExitBtn
                        disabled={
                            moment(ele?.expiresAt).isBefore() || ["MCX-FUT", "MCX-OPT", "COMMODITY"].includes(ele?.term)
                        }
                        tradeCardData={ele}
                        finCode={ele?.finCode}
                        type={ele?.recommendationType}
                        symbol={ele?.compName}
                        source={"live_trades"}
                        tab={ele?.term}
                        ele={ele}
                        isClosed={!ele?.open}
                        classNames="h-10 border border-primary flex w-full"
                        onClick={() => setOpen(false)}
                    />

                    <TradeCardBuyBtn
                        tradeCardData={ele}
                        finCode={ele?.finCode}
                        type={ele?.recommendationType}
                        symbol={ele?.compName}
                        source={"live_trades"}
                        tab={ele?.term}
                        ele={ele}
                        isClosed={!ele?.open}
                        classNames="h-10"
                        disabled={["MCX-FUT", "MCX-OPT", "COMMODITY"].includes(ele?.term)}
                        onClick={() => setOpen(false)}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TradeHistoryPopUp;
